"use client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { signIn } from "next-auth/react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

const schema = z.object({
  email: z.string().email("Informe um email válido"),
  password: z.string().min(6, "Senha deve ter pelo menos 6 caracteres"),
});

type FormData = z.infer<typeof schema>;

export default function LoginPage() {
  const router = useRouter();
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    const result = await signIn("credentials", {
      redirect: false,
      email: data.email,
      password: data.password,
    });

    setLoading(false);

    if (result?.error) {
      toast.error("Credenciais inválidas");
      return;
    }

    router.push(`/login/redirect`);
  };

  return (
    <div className="min-h-dvh flex items-center justify-center p-4 bg-[#050505] text-white selection:bg-primary selection:text-white relative overflow-hidden dark">
      {/* Background Glows */}
      <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-primary/20 blur-[180px] rounded-full mix-blend-screen pointer-events-none" />
      <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-purple-600/15 blur-[150px] rounded-full mix-blend-screen pointer-events-none" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_80%_at_50%_50%,#000_10%,transparent_100%)] pointer-events-none" />

      <div className="w-full max-w-md relative z-10">
        <Link href="/" className="inline-flex items-center gap-2 mb-8 text-white/50 hover:text-white transition-colors text-sm font-medium">
          <span>&larr;</span> Voltar para o site
        </Link>
        
        <div className="relative group">
          <div className="absolute -inset-1 bg-gradient-to-r from-primary/30 to-purple-500/30 rounded-3xl blur-xl opacity-50 transition duration-1000"></div>
          
          <div className="relative bg-[#0a0a0a]/90 backdrop-blur-2xl border border-white/10 rounded-3xl p-8 md:p-10 shadow-2xl">
            <div className="flex flex-col items-center text-center gap-4 mb-8">
              <div className="size-16 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mb-2">
                <Image src="/logo-white.png" alt="Agendei" width={32} height={32} />
              </div>
              <h1 className="text-3xl font-bold tracking-tight text-white">Bem-vindo de volta</h1>
              <p className="text-white/60 text-sm">
                Acesse seu painel e gerencie seus horários
              </p>
            </div>
            
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <div className="space-y-1">
                <Input
                  type="email"
                  placeholder="Seu e-mail"
                  autoComplete="email"
                  className="h-12 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-primary rounded-xl px-4"
                  {...register("email")}
                />
                {errors.email && (
                  <p className="mt-1 text-xs text-red-400 font-medium">
                    {errors.email.message}
                  </p>
                )}
              </div>
              
              <div className="space-y-1">
                <Input
                  type="password"
                  placeholder="Sua senha"
                  autoComplete="current-password"
                  className="h-12 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-primary rounded-xl px-4"
                  {...register("password")}
                />
                {errors.password && (
                  <p className="mt-1 text-xs text-red-400 font-medium">
                    {errors.password.message}
                  </p>
                )}
                <div className="text-right pt-2">
                  <Link
                    href="/forgot-password"
                    className="text-xs text-primary hover:text-primary/80 transition-colors font-medium"
                  >
                    Esqueceu sua senha?
                  </Link>
                </div>
              </div>

              <Button type="submit" disabled={loading} className="w-full h-12 rounded-xl text-base font-bold bg-white text-black hover:bg-white/90 shadow-[0_0_20px_rgba(255,255,255,0.1)] transition-all">
                {loading ? "Entrando…" : "Entrar no painel"}
              </Button>
            </form>
            
            <div className="mt-10 pt-8 border-t border-white/10 text-center space-y-4">
              <p className="text-sm text-white/50 font-medium">Ainda não tem uma conta?</p>
              <Button asChild className="w-full h-12 rounded-xl text-base font-bold bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-all">
                <Link href="/cadastro">
                  Criar conta grátis — 30 dias de teste
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
